import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Package,
  Download,
  Filter,
  ArrowDownCircle,
  ArrowUpCircle,
  Scale,
  PackagePlus,
} from "lucide-react";
import { productApi, warehouseApi, exportApi, transactionApi } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterWarehouse, setFilterWarehouse] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    description: "",
    unit_price: "",
    quantity: "",
    min_stock: "10",
    warehouse_id: "",
  });

  // Add Inventory Dialog state
  const [addInventoryOpen, setAddInventoryOpen] = useState(false);
  const [inventoryForm, setInventoryForm] = useState({
    product_id: "",
    transaction_type: "in",
    quantity: "",
    notes: "",
  });
  const [selectedProductForInventory, setSelectedProductForInventory] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, warehousesRes] = await Promise.all([
        productApi.getAllWithBalance(),
        warehouseApi.getAll(),
      ]);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        unit_price: parseFloat(formData.unit_price),
        quantity: parseInt(formData.quantity) || 0,
        min_stock: parseInt(formData.min_stock) || 10,
      };

      if (editingProduct) {
        await productApi.update(editingProduct.id, data);
        toast.success("Product updated");
      } else {
        await productApi.create(data);
        toast.success("Product created");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await productApi.delete(id);
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku,
      name: product.name,
      description: product.description,
      unit_price: product.unit_price.toString(),
      quantity: product.quantity.toString(),
      min_stock: product.min_stock.toString(),
      warehouse_id: product.warehouse_id,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      sku: "",
      name: "",
      description: "",
      unit_price: "",
      quantity: "",
      min_stock: "10",
      warehouse_id: warehouses[0]?.id || "",
    });
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      p.sku.toLowerCase().includes(search.toLowerCase()) ||
      p.name.toLowerCase().includes(search.toLowerCase());
    const matchesWarehouse =
      filterWarehouse === "all" || p.warehouse_id === filterWarehouse;
    return matchesSearch && matchesWarehouse;
  });

  const getWarehouseName = (id) => {
    return warehouses.find((w) => w.id === id)?.name || "Unknown";
  };

  const getStockStatus = (product) => {
    const balance = product.balance || 0;
    if (balance === 0) return { label: "Out of Stock", class: "badge-error" };
    if (balance <= product.min_stock) return { label: "Low Stock", class: "badge-warning" };
    return { label: "In Stock", class: "badge-success" };
  };

  const openAddInventoryDialog = (product) => {
    setSelectedProductForInventory(product);
    setInventoryForm({
      product_id: product.id,
      transaction_type: "in",
      quantity: "",
      notes: "",
    });
    setAddInventoryOpen(true);
  };

  const handleAddInventory = async (e) => {
    e.preventDefault();
    try {
      await transactionApi.create({
        product_id: inventoryForm.product_id,
        transaction_type: inventoryForm.transaction_type,
        quantity: parseInt(inventoryForm.quantity),
        notes: inventoryForm.notes,
      });
      toast.success(`Stock ${inventoryForm.transaction_type === "in" ? "added" : "removed"} successfully`);
      setAddInventoryOpen(false);
      setSelectedProductForInventory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update inventory");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Products</h1>
          <p className="text-slate-400">Manage your inventory items</p>
        </div>
        <div className="flex gap-3">
          <a
            href={exportApi.products()}
            className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition"
            data-testid="export-products-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </a>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition glow-blue hover-glow-blue"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Search by SKU or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-[#0B0E14] border-slate-700 text-white"
            data-testid="product-search"
          />
        </div>
        <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
          <SelectTrigger className="w-full md:w-48 bg-[#0B0E14] border-slate-700 text-white" data-testid="warehouse-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Warehouses" />
          </SelectTrigger>
          <SelectContent className="bg-[#151923] border-slate-700">
            <SelectItem value="all">All Warehouses</SelectItem>
            {warehouses.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      {filteredProducts.length > 0 ? (
        <div className="bg-[#151923] border border-slate-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Unit Price</th>
                  <th>
                    <span className="flex items-center gap-1">
                      <ArrowDownCircle className="w-3.5 h-3.5 text-green-400" />
                      In
                    </span>
                  </th>
                  <th>
                    <span className="flex items-center gap-1">
                      <ArrowUpCircle className="w-3.5 h-3.5 text-red-400" />
                      Out
                    </span>
                  </th>
                  <th>
                    <span className="flex items-center gap-1">
                      <Scale className="w-3.5 h-3.5 text-blue-400" />
                      Balance
                    </span>
                  </th>
                  <th>Total Value</th>
                  <th>Warehouse</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const status = getStockStatus(product);
                  const totalIn = product.total_in || 0;
                  const totalOut = product.total_out || 0;
                  const balance = product.balance || 0;
                  return (
                    <tr key={product.id} className="table-row-hover" data-testid={`product-row-${product.sku}`}>
                      <td className="font-mono text-blue-400">{product.sku}</td>
                      <td className="text-slate-200">{product.name}</td>
                      <td>${product.unit_price.toFixed(2)}</td>
                      <td>
                        <span className="text-green-400 font-medium">+{totalIn}</span>
                      </td>
                      <td>
                        <span className="text-red-400 font-medium">-{totalOut}</span>
                      </td>
                      <td>
                        <span className={`font-bold ${balance >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                          {balance >= 0 ? '+' : ''}{balance}
                        </span>
                      </td>
                      <td>${(balance * product.unit_price).toFixed(2)}</td>
                      <td className="text-slate-400">{getWarehouseName(product.warehouse_id)}</td>
                      <td>
                        <span className={`badge ${status.class}`}>{status.label}</span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openAddInventoryDialog(product)}
                            className="p-1.5 text-slate-400 hover:text-green-400 hover:bg-slate-800 rounded"
                            title="Add/Remove Inventory"
                            data-testid={`add-inventory-${product.sku}`}
                          >
                            <PackagePlus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditDialog(product)}
                            className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-slate-800 rounded"
                            data-testid={`edit-product-${product.sku}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded"
                            data-testid={`delete-product-${product.sku}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-[#151923] border border-slate-800 rounded-sm">
          <div className="empty-state">
            <Package className="empty-state-icon" />
            <p className="text-slate-400 mb-4">No products found</p>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-500"
            >
              Add Your First Product
            </Button>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">SKU</label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="e.g., SKU001"
                required
                disabled={!!editingProduct}
                data-testid="product-sku-input"
              />
            </div>
            <div>
              <label className="form-label">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="Product name"
                required
                data-testid="product-name-input"
              />
            </div>
            <div>
              <label className="form-label">Description</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="Optional description"
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Unit Price</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="0.00"
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <label className="form-label">Initial Quantity</label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="0"
                  data-testid="product-quantity-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Min Stock Alert</label>
                <Input
                  type="number"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="10"
                  data-testid="product-minstock-input"
                />
              </div>
              <div>
                <label className="form-label">Warehouse</label>
                <Select
                  value={formData.warehouse_id}
                  onValueChange={(value) => setFormData({ ...formData, warehouse_id: value })}
                >
                  <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="product-warehouse-select">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="product-submit-btn"
              >
                {editingProduct ? "Update" : "Create"} Product
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Inventory Dialog */}
      <Dialog open={addInventoryOpen} onOpenChange={setAddInventoryOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="w-5 h-5 text-green-400" />
              Add Inventory
            </DialogTitle>
          </DialogHeader>
          {selectedProductForInventory && (
            <form onSubmit={handleAddInventory} className="space-y-4">
              <div className="bg-[#0B0E14] p-4 rounded-sm border border-slate-700">
                <p className="text-slate-400 text-sm">Selected Product</p>
                <p className="text-white font-medium">{selectedProductForInventory.name}</p>
                <p className="text-blue-400 font-mono text-sm">{selectedProductForInventory.sku}</p>
                <p className="text-slate-400 text-sm mt-2">
                  Current Balance: <span className="text-blue-400 font-bold">{selectedProductForInventory.balance || 0}</span>
                </p>
              </div>

              <div>
                <Label className="form-label">Transaction Type</Label>
                <Select
                  value={inventoryForm.transaction_type}
                  onValueChange={(value) => setInventoryForm({ ...inventoryForm, transaction_type: value })}
                >
                  <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="inventory-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#151923] border-slate-700">
                    <SelectItem value="in">
                      <span className="flex items-center gap-2">
                        <ArrowDownCircle className="w-4 h-4 text-green-400" />
                        Stock In (Add)
                      </span>
                    </SelectItem>
                    <SelectItem value="out">
                      <span className="flex items-center gap-2">
                        <ArrowUpCircle className="w-4 h-4 text-red-400" />
                        Stock Out (Remove)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={inventoryForm.quantity}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="Enter quantity"
                  required
                  data-testid="inventory-quantity-input"
                />
              </div>

              <div>
                <Label className="form-label">Notes (Optional)</Label>
                <Input
                  value={inventoryForm.notes}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, notes: e.target.value })}
                  className="bg-[#0B0E14] border-slate-700"
                  placeholder="e.g., Received from supplier"
                  data-testid="inventory-notes-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setAddInventoryOpen(false)}
                  className="text-slate-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={inventoryForm.transaction_type === "in" 
                    ? "bg-green-600 hover:bg-green-500" 
                    : "bg-red-600 hover:bg-red-500"
                  }
                  data-testid="inventory-submit-btn"
                >
                  {inventoryForm.transaction_type === "in" ? "Add Stock" : "Remove Stock"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
